import React, { useEffect, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import Title from '../../components/Title';
import SmallButton from '../../components/SmallButton';
import { axiosKK } from '../../services/networkRequest';
import { downloadCsv } from '../../services/downloadBlob';
import errorMessage from '../../services/errorMessage';
import {
  InvoiceAdditionalFeeDTO,
  InvoiceWeekDTO,
} from '../../models/APIModels';

type Scope = 'partner' | 'warehouse';

// Warehouse invoices have a different shape — a single line-item list per month.
interface WarehouseInvoiceItem {
  label: string;
  quantity?: number;
  rate?: number;
  amount?: number;
  [k: string]: unknown;
}
interface WarehouseInvoiceResponse {
  month?: string;
  items: WarehouseInvoiceItem[];
  subtotal?: number;
  vat?: number;
  total?: number;
  isLocked?: boolean;
}
interface PartnerInvoicesResponse {
  month?: string;
  weeks: InvoiceWeekDTO[];
  courierName?: string;
  subtotal?: number;
  vat?: number;
  total?: number;
  isLocked?: boolean;
}

function monthISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function pence(p?: number | null): string {
  if (p == null) return '—';
  return `£${(p / 100).toFixed(2)}`;
}

function InvoicesPage({ history }: RouteComponentProps) {
  const [scope, setScope] = useState<Scope>('partner');
  const [month, setMonth] = useState<string>(monthISO);

  const [partnerData, setPartnerData] = useState<PartnerInvoicesResponse | null>(null);
  const [warehouseData, setWarehouseData] = useState<WarehouseInvoiceResponse | null>(null);

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [netError, setNetError] = useState<string>();
  const [banner, setBanner] = useState<string>();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNetError(undefined);
    setBanner(undefined);

    const url =
      scope === 'partner'
        ? '/logistics/dashboard/partner/invoices'
        : '/logistics/dashboard/warehouse/invoices';

    axiosKK
      .get(url, { params: { month } })
      .then(({ data }) => {
        if (cancelled) return;
        if (scope === 'partner') setPartnerData(data as PartnerInvoicesResponse);
        else setWarehouseData(data as WarehouseInvoiceResponse);
      })
      .catch((err) => {
        if (!cancelled) errorMessage(setNetError)(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [scope, month]);

  const exportCsv = () => {
    const url =
      scope === 'partner'
        ? '/logistics/dashboard/partner/invoices'
        : '/logistics/dashboard/warehouse/invoices';
    downloadCsv(url, `${scope}-invoices-${month}.csv`, { month }).catch((err) =>
      errorMessage(setNetError)(err)
    );
  };

  const lockWarehouseMonth = () => {
    setBusy(true);
    axiosKK
      .post(`/logistics/dashboard/warehouse/invoices/${month}/lock`)
      .then(() => setBanner(`Locked warehouse invoices for ${month}.`))
      .catch((err) => errorMessage(setNetError)(err))
      .finally(() => setBusy(false));
  };

  return (
    <>
      <section className="nav-status-bar">
        <div className="status-bar-content">
          <i className="fas fa-arrow-circle-left" />
          <SmallButton onClick={() => history.push('/admin/dashboard')}>Back</SmallButton>
        </div>
      </section>

      <Title>Invoices</Title>

      <div className="scope-tabs" style={{ padding: '0 50px', marginBottom: 20 }}>
        <button
          type="button"
          className={scope === 'partner' ? 'scope-tab scope-tab--active' : 'scope-tab'}
          onClick={() => setScope('partner')}
        >
          Partner
        </button>
        <button
          type="button"
          className={scope === 'warehouse' ? 'scope-tab scope-tab--active' : 'scope-tab'}
          onClick={() => setScope('warehouse')}
        >
          Warehouse
        </button>
      </div>

      {netError && <p className="error">{netError}</p>}
      {banner && <p className="success-banner">{banner}</p>}

      <section className="admin-partner-pricing">
        <div className="logistics-controls-container">
          <div className="filters-group">
            <div className="filter-item">
              <label>Month</label>
              <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
            </div>
            <div className="filter-item filter-item--action">
              <SmallButton onClick={exportCsv}>
                <i className="fas fa-file-csv" /> Export CSV
              </SmallButton>
            </div>
            {scope === 'warehouse' && (
              <div className="filter-item filter-item--action">
                <SmallButton onClick={lockWarehouseMonth}>
                  {busy ? '…' : 'Lock Month'}
                </SmallButton>
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <p>Loading…</p>
        ) : scope === 'partner' ? (
          <PartnerInvoices data={partnerData} />
        ) : (
          <WarehouseInvoices data={warehouseData} />
        )}
      </section>
    </>
  );
}

function PartnerInvoices({ data }: { data: PartnerInvoicesResponse | null }) {
  if (!data || !data.weeks || data.weeks.length === 0) {
    return <p>No invoice data for this month.</p>;
  }
  return (
    <>
      {data.weeks.map((w) => (
        <div key={w.weekCommencing} className="pricing-section">
          <h4>{w.displayLabel}</h4>
          <table className="pricing-table">
            <thead>
              <tr>
                <th>Line</th>
                <th>Qty / Count</th>
                <th>Rate</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              <InvoiceBoxRow label="Doorstep Collections" rate={w.doorstepCollections} />
              <InvoiceBoxRow label="Timeslot Collections" rate={w.timeslotCollections} />
              <InvoiceBoxRow label="Doorstep Returns" rate={w.doorstepReturns} />
              <InvoiceBoxRow label="Timeslot Returns" rate={w.timeslotReturns} />
              {w.minCharge > 0 && (
                <tr>
                  <td>Minimum Charge</td>
                  <td>—</td>
                  <td>—</td>
                  <td>{pence(w.minCharge)}</td>
                </tr>
              )}
              {w.failedDeliveries && w.failedDeliveries.count > 0 && (
                <tr>
                  <td>Failed Deliveries</td>
                  <td>{w.failedDeliveries.count}</td>
                  <td>{pence(w.failedDeliveries.rate)}</td>
                  <td>{pence(w.failedDeliveries.count * w.failedDeliveries.rate)}</td>
                </tr>
              )}
              {(w.additionalFees || [])
                .filter((f: InvoiceAdditionalFeeDTO) => f.quantity > 0)
                .map((f: InvoiceAdditionalFeeDTO, i: number) => (
                  <tr key={i}>
                    <td>{f.label}{f.applyMode ? ` (${f.applyMode})` : ''}</td>
                    <td>{f.quantity}</td>
                    <td>{pence(f.rate)}</td>
                    <td>{pence(f.quantity * f.rate)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      ))}

      {(data.subtotal != null || data.vat != null || data.total != null) && (
        <div className="pricing-section">
          <table className="pricing-table">
            <tbody>
              <tr>
                <td>Subtotal</td>
                <td>{pence(data.subtotal)}</td>
              </tr>
              <tr>
                <td>VAT (20%)</td>
                <td>{pence(data.vat)}</td>
              </tr>
              <tr>
                <td><strong>Total</strong></td>
                <td><strong>{pence(data.total)}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function InvoiceBoxRow({
  label,
  rate,
}: {
  label: string;
  rate: { boxes: number; rate: number };
}) {
  if (!rate || rate.boxes === 0) return null;
  return (
    <tr>
      <td>{label}</td>
      <td>{rate.boxes}</td>
      <td>{pence(rate.rate)}</td>
      <td>{pence(rate.boxes * rate.rate)}</td>
    </tr>
  );
}

function WarehouseInvoices({ data }: { data: WarehouseInvoiceResponse | null }) {
  if (!data || !data.items || data.items.length === 0) {
    return <p>No invoice data for this month.</p>;
  }
  return (
    <div className="pricing-section">
      <h4>{data.month || ''}</h4>
      <table className="pricing-table">
        <thead>
          <tr>
            <th>Line</th>
            <th>Qty</th>
            <th>Rate</th>
            <th>Amount</th>
          </tr>
        </thead>
        <tbody>
          {data.items
            .filter((it) => !(it.quantity === 0 && (it.amount || 0) === 0))
            .map((it, i) => (
              <tr key={i}>
                <td>{it.label}</td>
                <td>{it.quantity ?? '—'}</td>
                <td>{pence(it.rate)}</td>
                <td>{pence(it.amount)}</td>
              </tr>
            ))}
        </tbody>
      </table>
      {(data.subtotal != null || data.vat != null || data.total != null) && (
        <table className="pricing-table">
          <tbody>
            <tr>
              <td>Subtotal</td>
              <td>{pence(data.subtotal)}</td>
            </tr>
            <tr>
              <td>VAT (20%)</td>
              <td>{pence(data.vat)}</td>
            </tr>
            <tr>
              <td><strong>Total</strong></td>
              <td><strong>{pence(data.total)}</strong></td>
            </tr>
          </tbody>
        </table>
      )}
      {data.isLocked && <p className="pricing-hint">Month is locked.</p>}
    </div>
  );
}

export default withRouter(InvoicesPage);
